// ============================================================================
// STORE.WORKFLOW.JS — Workflow State + Event Persistence Layer
//
// Manages the mutable operational layer that sits on top of every immutable
// referral doc.  All writes go through writeBatch (state update + event
// create in a single atomic commit).
//
// Paths:
//   /tenants/{t}/referrals/{r}/workflow/state         — singleton mutable state
//   /tenants/{t}/referrals/{r}/workflowEvents/{eId}   — append-only event log
//
// Zero-PHI invariant:
//   • No free-text fields in workflow state or events.
//   • All status/decision values are controlled enums.
//   • Patient reference / age / sex are NOT stored here.
//
// Invariants:
//   1. Every referral MUST have a workflow/state doc (created in submit batch).
//   2. Every write to workflow/state MUST create exactly one workflowEvents doc.
//   3. Core fields (tenantId, referralId, createdAt, createdByUid,
//      sourceOutcomeStatus, schemaVersion) are immutable after creation.
// ============================================================================

import { getApp } from 'firebase/app';
import {
  getFirestore,
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { PATHS } from './firestorePaths.js';

// ── DB singleton ─────────────────────────────────────────────────────────────

function getDb() {
  return getFirestore(getApp());
}

// ── ID generator (simple enough; no collision risk for workflow events) ──────

function generateEventId() {
  return `wfe_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// ============================================================================
// ENUMS
// ============================================================================

export const WORKFLOW_STATUS = {
  NEW:               'NEW',
  NEEDS_INFO:        'NEEDS_INFO',
  READY_TO_SCHEDULE: 'READY_TO_SCHEDULE',
  SCHEDULED:         'SCHEDULED',
  SEEN:              'SEEN',
  CLOSED_LOOP:       'CLOSED_LOOP',
  CANCELLED:         'CANCELLED',
  NO_SHOW:           'NO_SHOW',
};

// Not-Yet outcome recovery progress. Lives on workflow/state.recoveryStage.
// Non-null ONLY for referrals whose Gate origin was NOT_YET
// (sourceOutcomeStatus === 'not_yet'). Intake-created NEEDS_INFO keeps null.
//   AWAITING_GP             — NOT_YET; GP must complete missing information
//   READY_FOR_INTAKE_REVIEW — GP re-submitted; Gate recovered to ROUTINE/URGENT
//   CLEARED                 — Intake claimed + cleared to READY_TO_SCHEDULE
export const RECOVERY_STAGE = {
  AWAITING_GP:             'AWAITING_GP',
  READY_FOR_INTAKE_REVIEW: 'READY_FOR_INTAKE_REVIEW',
  CLEARED:                 'CLEARED',
};

export const WORKFLOW_EVENT_TYPES = {
  WORKFLOW_CREATED:           'WORKFLOW_CREATED',
  OWNER_ASSIGNED:             'OWNER_ASSIGNED',
  OWNER_UNASSIGNED:           'OWNER_UNASSIGNED',
  STATUS_CHANGED:             'STATUS_CHANGED',
  TRIAGE_UPDATED:             'TRIAGE_UPDATED',
  SCHEDULED_SET:              'SCHEDULED_SET',
  SEEN_SET:                   'SEEN_SET',
  CLOSED_LOOP_SET:            'CLOSED_LOOP_SET',
  PRIORITY_CHANGED:           'PRIORITY_CHANGED',
  TRIAGE_ON_HOLD:             'TRIAGE_ON_HOLD',
  TRIAGE_HOLD_RELEASED:       'TRIAGE_HOLD_RELEASED',
  TRIAGE_APPROVED:            'TRIAGE_APPROVED',
  // Flow Quality instrumentation (idempotent — written once per referral)
  ENTERED_OPS:                'ENTERED_OPS',               // first intake claim or touch
  TRIAGE_EXCEPTION_RECORDED:  'TRIAGE_EXCEPTION_RECORDED', // first override/hold/escalation
  // Scheduler return-to-intake (READY_TO_SCHEDULE → NEEDS_INFO with reason)
  RETURNED_TO_INTAKE:         'RETURNED_TO_INTAKE',
  // Not-Yet recovery: emitted only when recoveryStage actually changes
  RECOVERY_STAGE_CHANGED:     'RECOVERY_STAGE_CHANGED',
  // Scheduler escape hatch (reschedule / cancel / no-show)
  APPOINTMENT_RESCHEDULED:    'APPOINTMENT_RESCHEDULED',
  APPOINTMENT_CANCELLED:      'APPOINTMENT_CANCELLED',
  NO_SHOW_SET:                'NO_SHOW_SET',
};

export const WORKFLOW_PRIORITY = {
  NORMAL: 'normal',
  HIGH:   'high',
  URGENT: 'urgent',
};

// Return-to-intake reason codes (scheduler sends READY_TO_SCHEDULE → NEEDS_INFO)
export const RETURN_REASON_CODES = {
  OUTDATED_ECG:               'OUTDATED_ECG',
  OUTDATED_ATTACHMENT:        'OUTDATED_ATTACHMENT',
  WRONG_DOCUMENT:             'WRONG_DOCUMENT',
  MISSING_REQUIRED_ARTIFACT:  'MISSING_REQUIRED_ARTIFACT',
  INVALID_CLEARANCE:          'INVALID_CLEARANCE',
  OTHER:                      'OTHER',
};

export const RETURN_REASON_LABELS = {
  OUTDATED_ECG:               'ECG is outdated',
  OUTDATED_ATTACHMENT:        'Attachment is outdated',
  WRONG_DOCUMENT:             'Wrong document uploaded',
  MISSING_REQUIRED_ARTIFACT:  'Required artifact is missing',
  INVALID_CLEARANCE:          'Clearance is invalid or expired',
  OTHER:                      'Other (see note)',
};

export const TRIAGE_DECISION = {
  NONE:          'none',
  OK:            'ok',
  URGENT_REVIEW: 'urgent_review',
  REDIRECT:      'redirect',
};

// Status options per role (for UI selectors)
export const STATUS_OPTIONS_BY_ROLE = {
  intake_coordinator: [
    WORKFLOW_STATUS.NEW,
    WORKFLOW_STATUS.NEEDS_INFO,
    WORKFLOW_STATUS.READY_TO_SCHEDULE,
  ],
  triage_nurse: [
    WORKFLOW_STATUS.NEEDS_INFO,
    WORKFLOW_STATUS.READY_TO_SCHEDULE,
  ],
  scheduler: [
    WORKFLOW_STATUS.SCHEDULED,
    WORKFLOW_STATUS.NEEDS_INFO,      // return-to-intake
    WORKFLOW_STATUS.READY_TO_SCHEDULE, // cancel-clinic (return for re-booking)
    WORKFLOW_STATUS.CANCELLED,       // cancel-patient
    WORKFLOW_STATUS.NO_SHOW,         // no-show (terminal)
  ],
  specialist: [
    WORKFLOW_STATUS.SEEN,
    WORKFLOW_STATUS.CLOSED_LOOP,
  ],
  tenant_admin: Object.values(WORKFLOW_STATUS),
  platform_admin: Object.values(WORKFLOW_STATUS),
};

// ============================================================================
// STATE MACHINE — canonical FSM + role allowlist (Stage 8)
// ============================================================================

/**
 * Canonical adjacency map: fromStatus → allowed toStatus values.
 * CLOSED_LOOP is terminal — no outgoing transitions.
 */
export const VALID_TRANSITIONS = {
  [WORKFLOW_STATUS.NEW]:               [WORKFLOW_STATUS.NEEDS_INFO, WORKFLOW_STATUS.READY_TO_SCHEDULE],
  [WORKFLOW_STATUS.NEEDS_INFO]:        [WORKFLOW_STATUS.READY_TO_SCHEDULE],
  [WORKFLOW_STATUS.READY_TO_SCHEDULE]: [WORKFLOW_STATUS.NEEDS_INFO, WORKFLOW_STATUS.SCHEDULED],
  [WORKFLOW_STATUS.SCHEDULED]:         [WORKFLOW_STATUS.SEEN, WORKFLOW_STATUS.SCHEDULED, WORKFLOW_STATUS.READY_TO_SCHEDULE, WORKFLOW_STATUS.CANCELLED, WORKFLOW_STATUS.NO_SHOW],
  [WORKFLOW_STATUS.SEEN]:              [WORKFLOW_STATUS.CLOSED_LOOP],
  [WORKFLOW_STATUS.CLOSED_LOOP]:       [], // terminal
  [WORKFLOW_STATUS.CANCELLED]:         [], // terminal — no care delivered
  [WORKFLOW_STATUS.NO_SHOW]:           [], // terminal — patient did not attend
};

/**
 * Role → allowed [fromStatus, toStatus] transition pairs.
 * The sentinel value 'all' grants unrestricted access (tenant_admin / platform_admin).
 */
export const ROLE_ALLOWED_TRANSITIONS = {
  intake_coordinator: [
    [WORKFLOW_STATUS.NEW,               WORKFLOW_STATUS.NEEDS_INFO],
    [WORKFLOW_STATUS.NEW,               WORKFLOW_STATUS.READY_TO_SCHEDULE],
    [WORKFLOW_STATUS.NEEDS_INFO,        WORKFLOW_STATUS.READY_TO_SCHEDULE],
    [WORKFLOW_STATUS.READY_TO_SCHEDULE, WORKFLOW_STATUS.NEEDS_INFO],
  ],
  triage_nurse: [
    [WORKFLOW_STATUS.NEW,               WORKFLOW_STATUS.NEEDS_INFO],
    [WORKFLOW_STATUS.NEW,               WORKFLOW_STATUS.READY_TO_SCHEDULE],
    [WORKFLOW_STATUS.NEEDS_INFO,        WORKFLOW_STATUS.READY_TO_SCHEDULE],
    [WORKFLOW_STATUS.READY_TO_SCHEDULE, WORKFLOW_STATUS.NEEDS_INFO],
  ],
  scheduler: [
    [WORKFLOW_STATUS.READY_TO_SCHEDULE, WORKFLOW_STATUS.SCHEDULED],
    [WORKFLOW_STATUS.READY_TO_SCHEDULE, WORKFLOW_STATUS.NEEDS_INFO], // return-to-intake
    [WORKFLOW_STATUS.SCHEDULED,         WORKFLOW_STATUS.SCHEDULED],         // reschedule
    [WORKFLOW_STATUS.SCHEDULED,         WORKFLOW_STATUS.READY_TO_SCHEDULE], // cancel-clinic
    [WORKFLOW_STATUS.SCHEDULED,         WORKFLOW_STATUS.CANCELLED],         // cancel-patient
    [WORKFLOW_STATUS.SCHEDULED,         WORKFLOW_STATUS.NO_SHOW],           // no-show
  ],
  specialist: [
    [WORKFLOW_STATUS.SCHEDULED, WORKFLOW_STATUS.SEEN],
    [WORKFLOW_STATUS.SEEN,      WORKFLOW_STATUS.CLOSED_LOOP],
  ],
  tenant_admin:   'all', // unrestricted
  platform_admin: 'all', // unrestricted
};

// ── Private guards ────────────────────────────────────────────────────────────

function assertValidTransition(fromStatus, toStatus) {
  const allowed = VALID_TRANSITIONS[fromStatus];
  if (!allowed) {
    throw new Error(`Unknown fromStatus: "${fromStatus}"`);
  }
  if (!allowed.includes(toStatus)) {
    throw new Error(
      `Invalid transition ${fromStatus} → ${toStatus}. Allowed: [${allowed.join(', ') || 'none — terminal state'}]`
    );
  }
}

function assertRoleAllowedTransition(role, fromStatus, toStatus) {
  const allowed = ROLE_ALLOWED_TRANSITIONS[role];
  if (!allowed) {
    throw new Error(`Role "${role}" is not permitted to change workflow status`);
  }
  if (allowed === 'all') return; // tenant_admin / platform_admin
  const ok = allowed.some(([f, t]) => f === fromStatus && t === toStatus);
  if (!ok) {
    throw new Error(
      `Role "${role}" cannot perform transition ${fromStatus} → ${toStatus}`
    );
  }
}

// ── Single validated entry point ──────────────────────────────────────────────

/**
 * Apply a validated workflow status transition atomically.
 *
 * 1. Reads fresh state from Firestore (never relies on stale caller-supplied state).
 * 2. Asserts the transition is in VALID_TRANSITIONS (FSM check).
 * 3. Asserts the role is allowed for this specific [from, to] pair.
 * 4. Injects required timestamp fields (scheduledAt, seenAt, closedAt/closedByUid).
 * 5. Writes status + timestamps + event atomically via commitWorkflowAction.
 *
 * Throws before any Firestore write if any invariant is violated.
 */
export async function applyStatusTransition({ tenantId, referralId, toStatus, actorUid, actorRole, extraStateFields = {}, extraEventFields = {} }) {
  const db = getDb();

  // 1. Read fresh state
  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const snap     = await getDoc(stateRef);
  if (!snap.exists()) {
    throw new Error('Workflow state document not found. Cannot apply status transition.');
  }
  const current    = snap.data();
  const fromStatus = current.status;

  // 2. FSM check
  assertValidTransition(fromStatus, toStatus);

  // 3. Role check
  assertRoleAllowedTransition(actorRole, fromStatus, toStatus);

  // 3a. Ownership check — an intake_coordinator must claim the referral before
  // changing its status (e.g. NEW → READY_TO_SCHEDULE / NEEDS_INFO). Mirrors the
  // Firestore rule; surfaces a clear message instead of a raw permission error.
  if (actorRole === 'intake_coordinator' && current.ownerUid !== actorUid) {
    throw new Error('Claim this referral before changing its status.');
  }

  // 3b. Not-Yet recovery gate — a referral whose Gate origin was NOT_YET may only be
  // cleared to READY_TO_SCHEDULE once the GP has re-completed it and Gate recovered the
  // outcome (recoveryStage === READY_FOR_INTAKE_REVIEW). Blocks premature clearance.
  if (
    toStatus === WORKFLOW_STATUS.READY_TO_SCHEDULE &&
    current.sourceOutcomeStatus === 'not_yet' &&
    current.recoveryStage !== RECOVERY_STAGE.READY_FOR_INTAKE_REVIEW
  ) {
    throw new Error('This referral is still awaiting GP completion and cannot be cleared to Ready to Schedule.');
  }

  // 4. Required timestamp fields per target status
  const now         = serverTimestamp();
  const extraFields = {};
  if (toStatus === WORKFLOW_STATUS.SCHEDULED) {
    extraFields['scheduling.scheduledAt']  = now;
    extraFields['scheduling.scheduledByUid'] = actorUid;
  } else if (toStatus === WORKFLOW_STATUS.SEEN) {
    extraFields['scheduling.seenAt']    = now;
    extraFields['scheduling.seenByUid'] = actorUid;
  } else if (toStatus === WORKFLOW_STATUS.CLOSED_LOOP) {
    extraFields['closedLoop.closedAt']    = now;
    extraFields['closedLoop.closedByUid'] = actorUid;
  } else if (toStatus === WORKFLOW_STATUS.CANCELLED) {
    extraFields['cancelled.cancelledAt']    = now;
    extraFields['cancelled.cancelledByUid'] = actorUid;
  } else if (toStatus === WORKFLOW_STATUS.NO_SHOW) {
    extraFields['noShow.noShowAt']       = now;
    extraFields['noShow.noShowMarkedBy'] = actorUid;
  }

  // 4a. Clear returnToIntake flag when intake re-clears a returned referral
  if (fromStatus === WORKFLOW_STATUS.NEEDS_INFO && toStatus === WORKFLOW_STATUS.READY_TO_SCHEDULE) {
    extraFields['returnToIntake.active'] = false;
    // Not-Yet recovery: Intake clearance closes the recovery lane.
    if (current.recoveryStage === RECOVERY_STAGE.READY_FOR_INTAKE_REVIEW) {
      extraFields.recoveryStage = RECOVERY_STAGE.CLEARED;
    }
  }

  // 4b. Flow Quality — Entered Ops: first time an intake_coordinator makes a
  //     state-changing touch on this referral. Idempotent: never overwritten.
  const extraEvents = [];
  const isFirstIntakeTouched = actorRole === 'intake_coordinator' && !current.enteredOpsAt;
  if (isFirstIntakeTouched) {
    extraFields.enteredOpsAt     = now;
    extraFields.enteredOpsBy     = actorUid;
    extraFields.enteredOpsSource = 'TOUCH';
    extraEvents.push({
      eventType:  WORKFLOW_EVENT_TYPES.ENTERED_OPS,
      fromStatus,
      toStatus,
      ownerUid:   current.ownerUid ?? null,
      actorUid,
      actorRole,
      source:     'TOUCH',
    });
  }

  // 4c. Appointment instance lockstep — any transition to SEEN must close the linked
  //     appointment instance as SEEN in the SAME atomic batch (covers generic SEEN
  //     paths like updateWorkflowStatus from WorkItemDrawer). Referrals with no
  //     instance pointer (legacy, booked before the ledger) are skipped. The event
  //     carries appointmentInstanceId for traceability and cohort consistency.
  const instanceId = current.currentAppointmentInstanceId ?? null;
  let appointmentInstanceUpdate = null;
  const seenEventFields = { ...extraEventFields };
  if (toStatus === WORKFLOW_STATUS.SEEN && instanceId) {
    appointmentInstanceUpdate = { instanceId, status: WORKFLOW_STATUS.SEEN, outcomeByUid: actorUid };
    seenEventFields.appointmentInstanceId = instanceId;
  }

  // 5. Atomic commit — extraStateFields injected by callers (e.g. scheduler appointment details)
  //    statusEnteredAt resets on every status change (timer for stuck detection).
  //    lastTouchedAt records any real intake action (claim, status change).
  await commitWorkflowAction(
    tenantId,
    referralId,
    { status: toStatus, statusUpdatedAt: now, statusEnteredAt: now, lastTouchedAt: now, updatedByUid: actorUid, ...extraFields, ...extraStateFields },
    {
      eventType:  WORKFLOW_EVENT_TYPES.STATUS_CHANGED,
      fromStatus,
      toStatus,
      ownerUid:   current.ownerUid ?? null,
      actorUid:   actorUid,
      actorRole:  actorRole,
    },
    extraEvents,
    seenEventFields,
    appointmentInstanceUpdate,
  );
}

// ============================================================================
// BUILDERS
// ============================================================================

/**
 * Build the initial workflow state document created on referral submit.
 * outcome status 'not_yet' → workflow status NEEDS_INFO; all others → NEW.
 */
export function buildInitialWorkflowState({ tenantId, referralId, createdByUid, outcomeStatus }) {
  const initialStatus = outcomeStatus === 'not_yet'
    ? WORKFLOW_STATUS.NEEDS_INFO
    : WORKFLOW_STATUS.NEW;

  return {
    tenantId,
    referralId,
    schemaVersion: 1,

    // Status machine
    status:          initialStatus,
    statusUpdatedAt: null, // will be set by serverTimestamp in batch
    statusEnteredAt: null, // will be set by serverTimestamp in batch — resets on every status change

    // Ownership
    ownerUid:         null,
    ownerAssignedAt:  null,

    // Derived from referral outcome (immutable after creation)
    sourceOutcomeStatus: outcomeStatus,

    // Not-Yet recovery progress. Non-null ONLY when this referral originated as
    // a NOT_YET Gate outcome; intake-created NEEDS_INFO keeps these null.
    //   recoveryStage:    AWAITING_GP | READY_FOR_INTAKE_REVIEW | CLEARED | null
    //   recoveredOutcome: the re-evaluated Gate outcome (routine|urgent) | null
    //   lastGpResponseAt: when the GP last re-submitted | null
    recoveryStage:    outcomeStatus === 'not_yet' ? RECOVERY_STAGE.AWAITING_GP : null,
    recoveredOutcome: null,
    lastGpResponseAt: null,

    // Priority — URGENT gate outcome maps to HIGH operational priority at creation.
    // Source: tenants/{tenantId}/referrals/{referralId}/workflow/state.priority
    priority: outcomeStatus === 'urgent' ? WORKFLOW_PRIORITY.HIGH : WORKFLOW_PRIORITY.NORMAL,

    // Triage subobject (non-PHI)
    triage: {
      triageDecision:   TRIAGE_DECISION.NONE,
      triageUpdatedAt:  null,
      triageByUid:      null,
    },

    // Scheduling handshake (non-PHI)
    scheduling: {
      scheduledAt:      null,
      scheduledByUid:   null,
      seenAt:           null,
      seenByUid:        null,
    },

    // Pointer to the active appointment instance in the appointmentInstances ledger.
    // Set when status becomes SCHEDULED; cleared when the instance is no longer active
    // (clinic cancellation). null while the referral has no live booked appointment.
    currentAppointmentInstanceId: null,

    // Closed loop (non-PHI)
    closedLoop: {
      closedAt:    null,
      closedByUid: null,
    },

    // No-show (non-PHI)
    noShow: {
      noShowAt:       null,
      noShowMarkedBy: null,
    },

    // Rework signals (Stage 3 will expand)
    rework: {
      missingItemsCount: 0,
      lastTouchAt:       null,
      attempts:          1,
    },

    // Flow Quality instrumentation (idempotent — written once per referral)
    // enteredOpsAt: set on first intake_coordinator claim or state-changing touch
    // triageExceptionAt: set on first triage override, hold, or priority escalation
    enteredOpsAt:      null,
    enteredOpsBy:      null,
    enteredOpsSource:  null, // 'CLAIM' | 'TOUCH'
    triageExceptionAt: null,
    triageExceptionType: null, // 'OVERRIDE' | 'ON_HOLD' | 'PRIORITY_ESCALATION'

    // Lifecycle
    createdAt:    null, // set by serverTimestamp in batch
    createdByUid,
    updatedAt:    null, // set by serverTimestamp in batch
    updatedByUid: createdByUid,
  };
}

/**
 * Build a workflow event document for the append-only audit trail.
 */
export function buildWorkflowEvent({
  tenantId,
  referralId,
  eventType,
  fromStatus = null,
  toStatus   = null,
  ownerUid   = null,
  actorUid,
  actorRole,
}) {
  return {
    tenantId,
    referralId,
    eventType,
    fromStatus,
    toStatus,
    ownerUid,
    actorUid,
    actorRole,
    createdAt: null, // set by serverTimestamp in batch
  };
}

// ============================================================================
// SUBMIT HOOK — called inside store.referrals.js commitBatch
// ============================================================================

/**
 * Add workflow/state + workflowEvents creation to an existing writeBatch.
 * Must be called BEFORE batch.commit().
 *
 * @param {WriteBatch} batch — already-created writeBatch
 * @param {{ tenantId, referralId, createdByUid, outcomeStatus }} params
 */
/**
 * Called in the rerun batch when a NOT_YET referral is re-submitted via GateView.
 * The rerun path is exclusive to referrals whose Gate origin was NOT_YET, so
 * workflow.status ALWAYS stays NEEDS_INFO here. Recovery is tracked via
 * recoveryStage, NOT by advancing the workflow status.
 *
 * A NOT_YET referral may become ROUTINE/URGENT by Gate re-evaluation, but it must
 * NOT move to READY_TO_SCHEDULE until Intake claims and clears it.
 *
 * Transition rules:
 *   still not_yet     → status NEEDS_INFO, recoveryStage stays AWAITING_GP.
 *                       No workflow/state write, no event (stage unchanged — keeps
 *                       the audit trail free of noisy unchanged-rerun events).
 *   routine / urgent  → status NEEDS_INFO, recoveryStage → READY_FOR_INTAKE_REVIEW,
 *                       recoveredOutcome set, lastGpResponseAt stamped, and exactly
 *                       one RECOVERY_STAGE_CHANGED event appended.
 *
 * Note: statusEnteredAt is NOT reset — the status does not change, so the
 * "time in NEEDS_INFO" stuck timer must keep running from the original entry.
 */
export function updateWorkflowOnRerun(batch, { tenantId, referralId, actorUid, actorRole, outcomeStatus }) {
  const recovered = outcomeStatus !== 'not_yet';

  // Still NOT_YET: no stage change → no workflow/state write, no event.
  if (!recovered) return;

  const db  = getDb();
  const now = serverTimestamp();

  // 1. Recovery: update only recovery-tracking fields. Status stays NEEDS_INFO.
  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  batch.update(stateRef, {
    recoveryStage:    RECOVERY_STAGE.READY_FOR_INTAKE_REVIEW,
    recoveredOutcome: outcomeStatus,        // 'routine' | 'urgent'
    lastGpResponseAt: now,
    updatedAt:        now,
    // Escalate priority to HIGH when gate outcome is urgent (mirrors first-submit logic).
    ...(outcomeStatus === 'urgent' ? { priority: WORKFLOW_PRIORITY.HIGH } : {}),
  });

  // 2. Append RECOVERY_STAGE_CHANGED event (invariant: stage change produces one event).
  //    fromStatus/toStatus both NEEDS_INFO — the workflow status did not move.
  const eventId  = generateEventId();
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, eventId));
  batch.set(eventRef, {
    ...buildWorkflowEvent({
      tenantId,
      referralId,
      eventType:  WORKFLOW_EVENT_TYPES.RECOVERY_STAGE_CHANGED,
      fromStatus: WORKFLOW_STATUS.NEEDS_INFO,
      toStatus:   WORKFLOW_STATUS.NEEDS_INFO,
      actorUid,
      actorRole: actorRole || 'gp_employee',
    }),
    createdAt:        now,
    recoveryStage:    RECOVERY_STAGE.READY_FOR_INTAKE_REVIEW,
    recoveredOutcome: outcomeStatus,
    note: 'Not-Yet recovered → Ready for Intake Review',
  });
}

export function ensureWorkflowOnSubmit(batch, { tenantId, referralId, createdByUid, outcomeStatus }) {
  const db = getDb();
  const now = serverTimestamp();

  // 1. workflow/state doc
  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const stateDoc = buildInitialWorkflowState({ tenantId, referralId, createdByUid, outcomeStatus });
  batch.set(stateRef, {
    ...stateDoc,
    createdAt:       now,
    updatedAt:       now,
    statusUpdatedAt: now,
    statusEnteredAt: now,
  });

  // 2. WORKFLOW_CREATED event doc
  const eventId  = generateEventId();
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, eventId));
  const eventDoc = buildWorkflowEvent({
    tenantId,
    referralId,
    eventType:  WORKFLOW_EVENT_TYPES.WORKFLOW_CREATED,
    fromStatus: null,
    toStatus:   stateDoc.status,
    actorUid:   createdByUid,
    actorRole:  'gp_employee', // submit is always a gp_employee or higher
  });
  batch.set(eventRef, { ...eventDoc, createdAt: now });
}

// ============================================================================
// READ FUNCTIONS
// ============================================================================

/**
 * Read the workflow/state singleton for a referral.
 * Returns the data object or null if the doc is missing (invalid state).
 */
export async function getWorkflowState(tenantId, referralId) {
  const db = getDb();
  const snap = await getDoc(doc(db, PATHS.workflowState(tenantId, referralId)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * List workflowEvents for a referral (newest first, max 20).
 */
export async function listWorkflowEvents(tenantId, referralId, maxResults = 20) {
  const db = getDb();
  const q = query(
    collection(db, PATHS.col.workflowEvents(tenantId, referralId)),
    orderBy('createdAt', 'desc'),
    limit(maxResults),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Load all referrals + their workflow states for the workqueue.
 * Strategy: query referrals (ordered by createdAt DESC), then parallel-fetch
 * workflow/state for each — single-tenant, tenant-scoped.
 *
 * @param {string} tenantId
 * @param {{ pageSize?, specialtyFilter? }} options
 * @returns {Promise<Array<{ referral, workflow }>>}
 */
export async function listWorkqueueItems(tenantId, { pageSize = 50, specialtyFilter = null } = {}) {
  const db = getDb();

  // Query referrals
  const constraints = [orderBy('createdAt', 'desc'), limit(pageSize)];
  if (specialtyFilter) {
    constraints.unshift(where('specialtyId', '==', specialtyFilter));
  }

  const referralQ = query(
    collection(db, PATHS.col.referrals(tenantId)),
    ...constraints,
  );

  const referralSnap = await getDocs(referralQ);
  if (referralSnap.empty) return [];

  // Parallel fetch workflow states
  const workflowPromises = referralSnap.docs.map((rDoc) =>
    getDoc(doc(db, PATHS.workflowState(tenantId, rDoc.id))),
  );
  const workflowSnaps = await Promise.all(workflowPromises);

  return referralSnap.docs.map((rDoc, i) => ({
    referral: { id: rDoc.id, ...rDoc.data() },
    workflow: workflowSnaps[i].exists()
      ? { id: workflowSnaps[i].id, ...workflowSnaps[i].data() }
      : null,
  }));
}

// ── Phase 2 helpers: query-by-mirror + canonical-truth inclusion ──────────────
//
// referral.workflowStatus is a trustworthy MIRROR (kept in sync by the
// onWorkflowStateChange_syncMirror trigger). It is used ONLY to discover
// candidate referrals efficiently. workflow/state.status remains the highest
// truth and decides final inclusion. Missing workflow/state → excluded and
// reported as legacy/noWorkflow; never repaired or fabricated here.

/**
 * Cursor-paginate a referrals query to exhaustion (no fixed result cap).
 * @param {import('firebase/firestore').CollectionReference} colRef
 * @param {Array} constraints  query constraints WITHOUT a cursor (must include orderBy + limit)
 * @param {number} pageSize    the limit used in constraints (loop stops when a page is short)
 * @param {number} maxPages    safety guard against runaway loops
 * @returns {Promise<Array>} all matching QueryDocumentSnapshots
 */
async function fetchAllByCursor(colRef, constraints, pageSize, maxPages = 200) {
  const out = [];
  let cursor = null;
  for (let page = 0; page < maxPages; page++) {
    const q = cursor
      ? query(colRef, ...constraints, startAfter(cursor))
      : query(colRef, ...constraints);
    const snap = await getDocs(q);
    if (snap.empty) break;
    out.push(...snap.docs);
    if (snap.docs.length < pageSize) break;
    cursor = snap.docs[snap.docs.length - 1];
    if (page === maxPages - 1) {
      console.warn(`[fetchAllByCursor] hit maxPages=${maxPages} (pageSize ${pageSize}) — result may be truncated`);
    }
  }
  return out;
}

/**
 * Read workflow/state per candidate referral and include ONLY those whose
 * canonical workflow/state.status is in the requested set. De-dupes by id.
 * Returns the canonical { referral, workflow } shape. Logs excluded records.
 *
 * @param {string} tenantId
 * @param {Array} refDocs              candidate referral QueryDocumentSnapshots
 * @param {Set<string>} requestedSet   canonical statuses valid for this loader
 * @param {string} label               for diagnostic logging
 */
async function attachCanonicalState(tenantId, refDocs, requestedSet, label) {
  const db = getDb();
  const BATCH = 50;

  // De-dupe candidates by referral id (in case multiple query streams overlap).
  const uniq = [];
  const seen = new Set();
  for (const d of refDocs) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    uniq.push(d);
  }

  const items = [];
  const noWorkflowIds = [];
  let mirrorLagExcluded = 0;

  for (let i = 0; i < uniq.length; i += BATCH) {
    const chunk = uniq.slice(i, i + BATCH);
    const wfSnaps = await Promise.all(
      chunk.map((rDoc) => getDoc(doc(db, PATHS.workflowState(tenantId, rDoc.id)))),
    );
    for (let j = 0; j < chunk.length; j++) {
      const wfSnap = wfSnaps[j];
      // No canonical truth → legacy/noWorkflow. Exclude; never fabricate state.
      if (!wfSnap.exists() || !wfSnap.data().status) {
        noWorkflowIds.push(chunk[j].id);
        continue;
      }
      const wf = { id: wfSnap.id, ...wfSnap.data() };
      // Canonical status decides inclusion (mirror was only for discovery).
      if (!requestedSet.has(wf.status)) {
        mirrorLagExcluded++;
        continue;
      }
      items.push({
        referral: { id: chunk[j].id, ...chunk[j].data() },
        workflow: wf,
      });
    }
  }

  if (noWorkflowIds.length) {
    console.warn(
      `[${label}] excluded ${noWorkflowIds.length} legacy/noWorkflow referral(s) (no workflow/state) — not repaired:`,
      noWorkflowIds.slice(0, 20),
    );
  }
  if (mirrorLagExcluded) {
    console.warn(
      `[${label}] excluded ${mirrorLagExcluded} referral(s) whose canonical workflow/state.status is outside the requested set (mirror lag).`,
    );
  }

  return items;
}

/**
 * Load all active referrals + their workflow states for the Ops Dashboard.
 *
 * Discovery: query referrals by the trustworthy mirror
 *   where workflowStatus in [NEW, NEEDS_INFO, READY_TO_SCHEDULE, SCHEDULED, SEEN]
 *   order by workflowStatusEnteredAt desc, cursor-paginated to exhaustion.
 * Inclusion: canonical workflow/state.status must be in the active set.
 * Missing workflow/state → excluded + reported (legacy/noWorkflow).
 *
 * @param {string} tenantId
 * @returns {Promise<Array<{ referral, workflow }>>}
 */
export async function listOpsActiveItems(tenantId) {
  const db = getDb();
  const ACTIVE = [
    WORKFLOW_STATUS.NEW,
    WORKFLOW_STATUS.NEEDS_INFO,
    WORKFLOW_STATUS.READY_TO_SCHEDULE,
    WORKFLOW_STATUS.SCHEDULED,
    WORKFLOW_STATUS.SEEN,
  ];
  const requestedSet = new Set(ACTIVE);
  const PAGE = 200;

  const candidates = await fetchAllByCursor(
    collection(db, PATHS.col.referrals(tenantId)),
    [
      where('workflowStatus', 'in', ACTIVE),
      orderBy('workflowStatusEnteredAt', 'desc'),
      limit(PAGE),
    ],
    PAGE,
  );

  if (candidates.length === 0) return [];
  return attachCanonicalState(tenantId, candidates, requestedSet, 'listOpsActiveItems');
}

/**
 * Load referrals relevant to a specialist / specialist-admin queue dashboard.
 *
 * Discovery (by mirror, cursor-paginated to exhaustion):
 *   • active:   workflowStatus in [SCHEDULED, SEEN]
 *   • terminal: workflowStatus in [CLOSED_LOOP, NO_SHOW] AND
 *               workflowStatusEnteredAt >= 30 days ago (bounds terminal volume)
 * Inclusion: canonical workflow/state.status must be in the requested set
 *   {SCHEDULED, SEEN, CLOSED_LOOP, NO_SHOW}. Terminal statuses are valid here
 *   (this loader serves tabs that request them) — not excluded.
 * Missing workflow/state → excluded + reported (legacy/noWorkflow).
 *
 * @param {string} tenantId
 * @returns {Promise<Array<{ referral, workflow }>>}
 */
export async function listSpecialistQueueItems(tenantId) {
  const db = getDb();
  const colRef = collection(db, PATHS.col.referrals(tenantId));
  const PAGE = 200;

  const ACTIVE   = [WORKFLOW_STATUS.SCHEDULED, WORKFLOW_STATUS.SEEN];
  const TERMINAL = [WORKFLOW_STATUS.CLOSED_LOOP, WORKFLOW_STATUS.NO_SHOW];
  const requestedSet = new Set([...ACTIVE, ...TERMINAL]);

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const thirtyDaysAgoTs = Timestamp.fromDate(thirtyDaysAgo);

  const [activeDocs, terminalDocs] = await Promise.all([
    fetchAllByCursor(
      colRef,
      [
        where('workflowStatus', 'in', ACTIVE),
        orderBy('workflowStatusEnteredAt', 'asc'),
        limit(PAGE),
      ],
      PAGE,
    ),
    fetchAllByCursor(
      colRef,
      [
        where('workflowStatus', 'in', TERMINAL),
        where('workflowStatusEnteredAt', '>=', thirtyDaysAgoTs),
        orderBy('workflowStatusEnteredAt', 'desc'),
        limit(PAGE),
      ],
      PAGE,
    ),
  ]);

  const candidates = [...activeDocs, ...terminalDocs];
  if (candidates.length === 0) return [];
  return attachCanonicalState(tenantId, candidates, requestedSet, 'listSpecialistQueueItems');
}

// ============================================================================
// WRITE FUNCTIONS (each: writeBatch → state update + event create)
// ============================================================================

// ── Internal helper ──────────────────────────────────────────────────────────

/**
 * Commit a workflow state update + one primary event + optional extra events
 * in a single writeBatch.
 *
 * @param {string}   tenantId
 * @param {string}   referralId
 * @param {object}   stateUpdate  — fields to merge into workflow/state
 * @param {object}   eventParams  — params for buildWorkflowEvent (minus tenantId/referralId)
 * @param {object[]} extraEvents  — optional fully-built event objects (minus createdAt);
 *                                  written as additional workflowEvents docs in the same batch.
 *                                  Each object must include at minimum: eventType, actorUid, actorRole.
 */
async function commitWorkflowAction(tenantId, referralId, stateUpdate, eventParams, extraEvents = [], extraEventFields = {}, appointmentInstanceUpdate = null) {
  const db  = getDb();
  const now = serverTimestamp();

  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const eventId  = generateEventId();
  const eventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, eventId));

  const eventDoc = buildWorkflowEvent({ tenantId, referralId, ...eventParams });

  const batch = writeBatch(db);
  batch.update(stateRef, { ...stateUpdate, updatedAt: now });
  batch.set(eventRef, { ...eventDoc, ...extraEventFields, createdAt: now });

  // Appointment instance ledger: keep the linked instance in lockstep with the
  // status transition (e.g. SCHEDULED → SEEN sets the instance outcome to SEEN)
  // in the SAME atomic batch — prevents workflow/state and appointmentInstances
  // from drifting (split-brain) and keeps the appointment outcome cohort truthful.
  // Status literal (not the store.scheduling enum) avoids a circular import.
  if (appointmentInstanceUpdate && appointmentInstanceUpdate.instanceId) {
    const instanceRef = doc(db, PATHS.appointmentInstance(tenantId, referralId, appointmentInstanceUpdate.instanceId));
    batch.update(instanceRef, {
      status:       appointmentInstanceUpdate.status,
      outcomeAt:    now,
      outcomeByUid: appointmentInstanceUpdate.outcomeByUid ?? null,
      updatedAt:    now,
    });
  }

  // Extra events — e.g. ENTERED_OPS or TRIAGE_EXCEPTION_RECORDED written alongside
  // the primary event in the same atomic batch.
  for (const extraEvt of extraEvents) {
    const extraEventId  = generateEventId();
    const extraEventRef = doc(db, PATHS.workflowEvent(tenantId, referralId, extraEventId));
    batch.set(extraEventRef, { tenantId, referralId, ...extraEvt, createdAt: now });
  }

  // Denormalize workflowStatus + workflowStatusEnteredAt onto the parent referral doc
  // whenever status changes. This is the query field that enables the GP inbox to
  // surface ROUTINE/URGENT referrals set to NEEDS_INFO without subcollection joins.
  // Canonical source of truth remains workflow/state.status.
  if (stateUpdate.status !== undefined) {
    const referralRef = doc(db, PATHS.referral(tenantId, referralId));
    batch.update(referralRef, {
      workflowStatus:          stateUpdate.status,
      workflowStatusEnteredAt: now,
    });
  }

  await batch.commit();
}

// ── Ownership ────────────────────────────────────────────────────────────────

/**
 * Assign the caller as owner of a work item.
 *
 * Flow Quality: when an intake_coordinator claims ownership for the first time
 * (enteredOpsAt is null), stamps enteredOpsAt/By/Source = CLAIM on the state doc
 * and appends an ENTERED_OPS event in the same atomic batch.
 *
 * @param {string} tenantId
 * @param {string} referralId
 * @param {object} currentWorkflow — caller-supplied workflow state (used for fromStatus
 *                                   when fresh read fails; fresh state is always re-read)
 * @param {string} uid             — actor's uid (becomes owner)
 * @param {string} role            — actor's role (logged in event)
 */
export async function claimOwner(tenantId, referralId, currentWorkflow, uid, role) {
  // Always read fresh state to detect enteredOpsAt reliably.
  const db      = getDb();
  const snap    = await getDoc(doc(db, PATHS.workflowState(tenantId, referralId)));
  const current = snap.exists() ? snap.data() : (currentWorkflow ?? {});
  const now     = serverTimestamp();

  // Flow Quality — idempotent first intake claim marker.
  const isFirstIntakeClaim = role === 'intake_coordinator' && !current.enteredOpsAt;

  const stateUpdate = {
    ownerUid:        uid,
    ownerAssignedAt: now,
    lastTouchedAt:   now,
    updatedByUid:    uid,
  };
  const extraEvents = [];

  if (isFirstIntakeClaim) {
    stateUpdate.enteredOpsAt     = now;
    stateUpdate.enteredOpsBy     = uid;
    stateUpdate.enteredOpsSource = 'CLAIM';
    extraEvents.push({
      eventType:  WORKFLOW_EVENT_TYPES.ENTERED_OPS,
      fromStatus: current.status ?? null,
      toStatus:   current.status ?? null,
      ownerUid:   uid,
      actorUid:   uid,
      actorRole:  role,
      source:     'CLAIM',
    });
  }

  await commitWorkflowAction(
    tenantId,
    referralId,
    stateUpdate,
    {
      eventType:  WORKFLOW_EVENT_TYPES.OWNER_ASSIGNED,
      fromStatus: current.status ?? null,
      toStatus:   current.status ?? null,
      ownerUid:   uid,
      actorUid:   uid,
      actorRole:  role,
    },
    extraEvents,
  );
}

/**
 * Unassign (release) ownership from a work item.
 */
export async function releaseOwner(tenantId, referralId, currentWorkflow, uid, role) {
  await commitWorkflowAction(
    tenantId,
    referralId,
    {
      ownerUid:        null,
      ownerAssignedAt: null,
      updatedByUid:    uid,
    },
    {
      eventType:  WORKFLOW_EVENT_TYPES.OWNER_UNASSIGNED,
      fromStatus: currentWorkflow?.status ?? null,
      toStatus:   currentWorkflow?.status ?? null,
      ownerUid:   null,
      actorUid:   uid,
      actorRole:  role,
    },
  );
}

// ── Status ───────────────────────────────────────────────────────────────────

/**
 * Update the workflow status.
 * Delegates to applyStatusTransition — FSM + role validated, reads fresh state.
 * currentWorkflow param is retained for API compatibility but ignored (_).
 */
export async function updateWorkflowStatus(tenantId, referralId, _currentWorkflow, toStatus, uid, role) {
  return applyStatusTransition({ tenantId, referralId, toStatus, actorUid: uid, actorRole: role });
}

// ── Priority ─────────────────────────────────────────────────────────────────

/**
 * Update the workflow priority flag.
 */
export async function updateWorkflowPriority(tenantId, referralId, currentWorkflow, priority, uid, role) {
  await commitWorkflowAction(
    tenantId,
    referralId,
    {
      priority,
      updatedByUid: uid,
    },
    {
      eventType:  WORKFLOW_EVENT_TYPES.PRIORITY_CHANGED,
      fromStatus: currentWorkflow?.status ?? null,
      toStatus:   currentWorkflow?.status ?? null,
      ownerUid:   currentWorkflow?.ownerUid ?? null,
      actorUid:   uid,
      actorRole:  role,
    },
  );
}

// ── Triage ───────────────────────────────────────────────────────────────────

/**
 * Set the triage decision (triage_nurse only) — ATOMIC (S5 / G3).
 *
 * The triage decision, the workflow/state mutation, and the workflowEvents record
 * are written together in ONE writeBatch:
 *   - toStatus set  → FSM-validated status transition + triage.* fields + a
 *     STATUS_CHANGED event (carrying triageDecision), via applyStatusTransition's
 *     extraStateFields / extraEventFields (single batch).
 *   - toStatus null → triage.* fields + a TRIAGE_UPDATED event (single batch).
 *
 * On any failure the whole batch is rejected — the status never advances without
 * the triage decision, and a state mutation never lacks its event (fail-fast, no
 * partial truth). currentWorkflow is retained for API compatibility but ignored.
 *
 * @param {string}      triageDecision — TRIAGE_DECISION enum value
 * @param {string|null} toStatus       — optional status transition (validated by FSM)
 */
export async function setTriageDecision(tenantId, referralId, _currentWorkflow, triageDecision, uid, toStatus = null) {
  const triageStateFields = {
    'triage.triageDecision':  triageDecision,
    'triage.triageUpdatedAt': serverTimestamp(),
    'triage.triageByUid':     uid,
  };

  if (toStatus) {
    // Status transition + triage decision (state) + STATUS_CHANGED event (carrying
    // triageDecision) committed ATOMICALLY by the canonical FSM helper — the triage
    // fields ride in the SAME batch as the status change. No second standalone write.
    await applyStatusTransition({
      tenantId, referralId, toStatus, actorUid: uid, actorRole: 'triage_nurse',
      extraStateFields: triageStateFields,
      extraEventFields: { triageDecision },
    });
    return;
  }

  // No status change requested → atomic triage-only write: triage fields + a
  // TRIAGE_UPDATED event in ONE batch (a state mutation never lacks its event).
  const db       = getDb();
  const stateRef = doc(db, PATHS.workflowState(tenantId, referralId));
  const snap     = await getDoc(stateRef);
  if (!snap.exists()) {
    throw new Error('Workflow state document not found. Cannot set triage decision.');
  }
  const fromStatus = snap.data().status;

  await commitWorkflowAction(
    tenantId,
    referralId,
    { ...triageStateFields, updatedByUid: uid },
    {
      eventType:  WORKFLOW_EVENT_TYPES.TRIAGE_UPDATED,
      fromStatus,
      toStatus:   fromStatus,
      actorUid:   uid,
      actorRole:  'triage_nurse',
    },
    [],
    { triageDecision },
  );
}

// ── Scheduling ───────────────────────────────────────────────────────────────

/**
 * Mark the referral as scheduled (scheduler only).
 * Always transitions status to SCHEDULED.
 * @param {Date|number} scheduledAt — JS Date or epoch ms
 */
export async function setScheduled(tenantId, referralId, currentWorkflow, scheduledAt, uid) {
  const scheduledTs = scheduledAt instanceof Date
    ? scheduledAt
    : new Date(scheduledAt);

  await commitWorkflowAction(
    tenantId,
    referralId,
    {
      'scheduling.scheduledAt':    scheduledTs,
      'scheduling.scheduledByUid': uid,
      status:          WORKFLOW_STATUS.SCHEDULED,
      statusUpdatedAt: serverTimestamp(),
      updatedByUid:    uid,
    },
    {
      eventType:  WORKFLOW_EVENT_TYPES.SCHEDULED_SET,
      fromStatus: currentWorkflow?.status ?? null,
      toStatus:   WORKFLOW_STATUS.SCHEDULED,
      ownerUid:   currentWorkflow?.ownerUid ?? null,
      actorUid:   uid,
      actorRole:  'scheduler',
    },
  );
}

/**
 * Mark the referral as seen by specialist (scheduler only).
 * Always transitions status to SEEN.
 * @param {Date|number} seenAt — JS Date or epoch ms
 */
export async function setSeen(tenantId, referralId, currentWorkflow, seenAt, uid) {
  const seenTs = seenAt instanceof Date ? seenAt : new Date(seenAt);

  await commitWorkflowAction(
    tenantId,
    referralId,
    {
      'scheduling.seenAt':    seenTs,
      'scheduling.seenByUid': uid,
      status:          WORKFLOW_STATUS.SEEN,
      statusUpdatedAt: serverTimestamp(),
      updatedByUid:    uid,
    },
    {
      eventType:  WORKFLOW_EVENT_TYPES.SEEN_SET,
      fromStatus: currentWorkflow?.status ?? null,
      toStatus:   WORKFLOW_STATUS.SEEN,
      ownerUid:   currentWorkflow?.ownerUid ?? null,
      actorUid:   uid,
      actorRole:  'scheduler',
    },
    [],
    currentWorkflow?.currentAppointmentInstanceId
      ? { appointmentInstanceId: currentWorkflow.currentAppointmentInstanceId }
      : {},
    currentWorkflow?.currentAppointmentInstanceId
      ? { instanceId: currentWorkflow.currentAppointmentInstanceId, status: WORKFLOW_STATUS.SEEN, outcomeByUid: uid }
      : null,
  );
}

// ── Closed Loop ───────────────────────────────────────────────────────────────

/**
 * Mark the referral as closed loop / consult note returned (specialist only).
 * Always transitions status to CLOSED_LOOP.
 */
export async function setClosedLoop(tenantId, referralId, currentWorkflow, uid) {
  const now = new Date();

  await commitWorkflowAction(
    tenantId,
    referralId,
    {
      'closedLoop.closedAt':    now,
      'closedLoop.closedByUid': uid,
      status:          WORKFLOW_STATUS.CLOSED_LOOP,
      statusUpdatedAt: serverTimestamp(),
      updatedByUid:    uid,
    },
    {
      eventType:  WORKFLOW_EVENT_TYPES.CLOSED_LOOP_SET,
      fromStatus: currentWorkflow?.status ?? null,
      toStatus:   WORKFLOW_STATUS.CLOSED_LOOP,
      ownerUid:   currentWorkflow?.ownerUid ?? null,
      actorUid:   uid,
      actorRole:  'specialist',
    },
  );
}
